import { Injectable, Signal, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: number;
  type: ToastType;
  title: string;
  message: string;
}

const DEFAULT_DURATION_MS = 4000;

/**
 * Signal-backed toast service. The `ToastHostComponent` rendered inside
 * `AppLayoutComponent` subscribes to `toasts` and animates each entry.
 * Public API preserved from the prior `MatSnackBar` implementation so
 * callers need no changes.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toasts = signal<ToastItem[]>([]);
  private nextId = 1;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  readonly toasts: Signal<ToastItem[]> = this._toasts.asReadonly();

  success(title: string, message = '', ms = DEFAULT_DURATION_MS): void {
    this.show('success', title, message, ms);
  }

  error(title: string, message = '', ms = DEFAULT_DURATION_MS): void {
    this.show('error', title, message, ms);
  }

  warning(title: string, message = '', ms = DEFAULT_DURATION_MS): void {
    this.show('warning', title, message, ms);
  }

  info(title: string, message = '', ms = DEFAULT_DURATION_MS): void {
    this.show('info', title, message, ms);
  }

  show(type: ToastType | string, title: string, message = '', ms = DEFAULT_DURATION_MS): void {
    const normalisedType = this.normalizeType(type);
    const id = this.nextId++;
    const toast: ToastItem = { id, type: normalisedType, title, message };
    this._toasts.update(list => [...list, toast]);
    if (ms > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), ms)
      );
    }
  }

  dismiss(id?: number): void {
    if (id === undefined) {
      this.timers.forEach(clearTimeout);
      this.timers.clear();
      this._toasts.set([]);
      return;
    }
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this._toasts.update(list => list.filter(t => t.id !== id));
  }

  private normalizeType(type: ToastType | string): ToastType {
    return type === 'success' || type === 'error' || type === 'warning' ? type : 'info';
  }
}
